import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { eur, num, fmtDate, fmtDateTime, initials } from "@/lib/format";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneField } from "@/components/common/PhoneField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BeforeAfterSlider } from "@/components/common/BeforeAfterSlider";
import { ReferralPanel } from "@/components/common/ReferralPanel";
import { DirectSaleDialog } from "@/components/common/DirectSaleDialog";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/constants";
import { ACQUISITION_SOURCES } from "@/lib/constants";
import { ClientSearch } from "@/components/common/ClientSearch";
import { toast } from "sonner";
import { ArrowLeft, Plus, Loader2, Award, Wallet, Gift, Camera, Trash2, ImageIcon, Pencil, Package as PkgIcon, RefreshCw, CalendarClock, AlertTriangle } from "lucide-react";

// Le foto private sono servite dal backend: l'autenticazione avviene tramite
// il cookie httpOnly (inviato automaticamente dal browser sui tag <img> same-site).
const photoUrl = (path) => `${API}/files/${path}`;
// Slider Prima/Dopo (Fase 5.2.1 - Blocco D)

const Stat = ({ label, value }) => (
  <Card className="p-4"><div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-1 font-heading text-2xl font-semibold tabular">{value}</div></Card>
);

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Ritorno all'agenda sull'appuntamento di partenza (deep-link riusato in Agenda).
  const fromAgenda = searchParams.get("from") === "agenda";
  const backApptId = searchParams.get("appt");
  const backDate = searchParams.get("date");
  const goBack = () => {
    if (fromAgenda && backApptId && backDate) {
      nav(`/agenda?date=${backDate}&appt=${backApptId}`);
    } else {
      nav("/clienti");
    }
  };
  const [d, setD] = useState(null);
  const [formulaOpen, setFormulaOpen] = useState(false);
  const [formula, setFormula] = useState({ technique: "", formula: "", oxidant: "", grams: "", processing_min: 30, toning: "", notes: "" });

  const { user } = useAuth();
  const canEdit = hasPerm(user, "clients.write");
  const isAdmin = user?.role === "admin";
  const load = useCallback(() => api.get(`/clients/${id}`).then((r) => setD(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  // --- Saldo cliente (insoluti + crediti) ---
  const [bal, setBal] = useState({ open_debts_total: 0, credit_available: 0, debts: [], movements: [] });
  const loadBal = useCallback(() => api.get(`/clients/${id}/balance`).then((r) => setBal(r.data)).catch(() => {}), [id]);
  useEffect(() => { loadBal(); }, [loadBal]);
  const [debtPay, setDebtPay] = useState(null);
  const [debtAmt, setDebtAmt] = useState("");
  const [debtMethod, setDebtMethod] = useState("Contanti");
  const [addCreditOpen, setAddCreditOpen] = useState(false);
  const [creditAmt, setCreditAmt] = useState("");
  const [creditNote, setCreditNote] = useState("");
  const [busyBal, setBusyBal] = useState(false);
  const refreshBalAll = () => { load(); loadBal(); };

  // --- Annulla pacchetto inserito per errore ---
  const [voidPkg, setVoidPkg] = useState(null);      // preview { package, sale, usages, used_total, ... }
  const [voidReason, setVoidReason] = useState("");
  const [voidBusy, setVoidBusy] = useState(false);
  const openVoidPkg = async (p) => {
    try {
      const { data } = await api.get(`/client-packages/${p.id}/preview-void`);
      setVoidReason(""); setVoidPkg(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const confirmVoidPkg = async () => {
    if (!voidPkg) return;
    if (!voidReason.trim()) { toast.error("Inserisci la motivazione"); return; }
    setVoidBusy(true);
    try {
      const { data } = await api.post(`/client-packages/${voidPkg.package.id}/void`, {
        reason: voidReason.trim(), confirm_used: voidPkg.used_total > 0 });
      toast.success(data.sale_cancelled ? "Pacchetto annullato e vendita collegata stornata" : "Pacchetto annullato");
      setVoidPkg(null); setVoidReason(""); refreshBalAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setVoidBusy(false); }
  };

  const openDebtPay = (debt) => { setDebtPay(debt); setDebtAmt(String(debt.remaining)); setDebtMethod("Contanti"); };
  const submitDebtPay = async () => {
    const a = Number(debtAmt);
    if (!debtPay || isNaN(a) || a <= 0) { toast.error("Importo non valido"); return; }
    setBusyBal(true);
    try {
      await api.post(`/clients/${id}/debts/${debtPay.id}/pay`, { amount: Number(a.toFixed(2)), method: debtMethod });
      toast.success("Insoluto incassato"); setDebtPay(null); setDebtAmt(""); refreshBalAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusyBal(false); }
  };
  const submitAddCredit = async () => {
    const a = Number(creditAmt);
    if (isNaN(a) || a <= 0) { toast.error("Importo non valido"); return; }
    setBusyBal(true);
    try {
      await api.post(`/clients/${id}/credit`, { amount: Number(a.toFixed(2)), note: creditNote });
      toast.success("Credito aggiunto"); setAddCreditOpen(false); setCreditAmt(""); setCreditNote(""); refreshBalAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusyBal(false); }
  };
  const cancelDebt = async (debt) => {
    const reason = window.prompt("Motivo dell'annullamento dell'insoluto:");
    if (!reason) return;
    try {
      await api.post(`/clients/${id}/debts/${debt.id}/cancel`, { reason });
      toast.success("Insoluto annullato"); refreshBalAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const BAL_METHODS = ["Contanti", "Carta", "Bancomat", "Bonifico"];
  const debtStatusLabel = { open: "Aperto", partial: "Parzialmente pagato", paid: "Pagato", cancelled: "Annullato" };

  const deleteProfile = async () => {
    if (!window.confirm(`Eliminare il profilo di ${d.client.first_name} ${d.client.last_name}?\n\nLo storico vendite e il fatturato NON verranno eliminati e le statistiche del negozio resteranno invariate. Il profilo verrà solo rimosso dagli elenchi.`)) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Profilo eliminato (fatturato e statistiche invariati)");
      nav("/clienti");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore durante l'eliminazione"); }
  };

  // Tutte le email associate (accesso possibile con ognuna) — utile dopo un'unione profili
  const [emails, setEmails] = useState([]);
  useEffect(() => { api.get(`/clients/${id}/emails`).then((r) => setEmails(r.data.emails || [])).catch(() => setEmails([])); }, [id]);

  // --- Modifica profilo cliente ---
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(null);
  const [referrerClient, setReferrerClient] = useState(null);
  const openEdit = () => {
    const c = d.client;
    setReferrerClient(c.referrer_id ? { id: c.referrer_id, first_name: c.referrer_name || c.referrer_name_raw || "Cliente collegata", last_name: "" } : null);
    setEdit({
      first_name: c.first_name || "", last_name: c.last_name || "",
      phone: c.phone || "", email: c.email || "",
      birthdate: (c.birthdate || "").split("T")[0] || "",
      tags: (c.tags || []).join(", "), allergies: c.allergies || "",
      notes: c.notes || "", marketing_consent: !!c.marketing_consent,
      patch_test: !!c.patch_test,
      acquisition_source: c.acquisition_source || "",
      referrer_name_raw: c.referrer_name_raw || "",
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!edit.first_name.trim()) { toast.error("Il nome è obbligatorio"); return; }
    if (edit.acquisition_source === "amica" && !referrerClient && !edit.referrer_name_raw.trim()) {
      toast.error("Indica quale amica l'ha presentata (seleziona una cliente o scrivi il nome)"); return;
    }
    setSaving(true);
    try {
      const payload = {
        first_name: edit.first_name.trim(), last_name: edit.last_name.trim(),
        phone: edit.phone.trim(), email: edit.email.trim(),
        birthdate: edit.birthdate || null,
        tags: edit.tags.split(",").map((t) => t.trim()).filter(Boolean),
        allergies: edit.allergies.trim(), notes: edit.notes.trim(),
        marketing_consent: edit.marketing_consent, patch_test: edit.patch_test,
        acquisition_source: edit.acquisition_source || "",
        referrer_id: referrerClient?.id || "",
        referrer_name_raw: edit.referrer_name_raw?.trim() || "",
      };
      await api.put(`/clients/${id}`, payload);
      toast.success("Profilo aggiornato");
      setEditOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  // --- Trasformazioni & Foto (Fase 5.1) ---
  const [photos, setPhotos] = useState([]);
  const [cmpB, setCmpB] = useState("");
  const [cmpA, setCmpA] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pKind, setPKind] = useState("before");
  const [pLabel, setPLabel] = useState("");
  const [viewer, setViewer] = useState(null);
  const loadPhotos = useCallback(() => api.get(`/clients/${id}/photos`).then((r) => setPhotos(r.data)).catch(() => {}), [id]);
  useEffect(() => { loadPhotos(); }, [loadPhotos]);
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un'immagine"); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", pKind); fd.append("label", pLabel);
    setUploading(true);
    try {
      await api.post(`/clients/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto caricata"); setPLabel(""); loadPhotos();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore caricamento"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const deletePhoto = async (pid) => {
    await api.delete(`/clients/${id}/photos/${pid}`); toast.success("Foto rimossa"); loadPhotos();
  };

  const addFormula = async () => {
    await api.post(`/clients/${id}/formulas`, formula);
    toast.success("Formula aggiunta");
    setFormulaOpen(false); setFormula({ technique: "", formula: "", oxidant: "", grams: "", processing_min: 30, toning: "", notes: "" });
    load();
  };

  if (!d) return <div className="h-64 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--hi-gold))]" /></div>;
  const c = d.client, s = d.stats, tc = d.technical_card;

  return (
    <div>
      <Button variant="ghost" className="mb-4 -ml-2" onClick={goBack} data-testid="client-back"><ArrowLeft className="h-4 w-4 mr-1" /> {fromAgenda && backApptId ? "Torna all'agenda" : "Clienti"}</Button>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16"><AvatarFallback className="bg-[hsl(var(--hi-gold-soft))] text-[hsl(var(--hi-gold))] text-xl font-semibold">{initials(`${c.first_name} ${c.last_name}`)}</AvatarFallback></Avatar>
          <div>
            <h1 className="font-heading text-3xl font-semibold">{c.first_name} {c.last_name}</h1>
            <div className="text-sm text-muted-foreground">{c.phone} · {c.email}</div>
            {emails.length > 1 && (
              <div className="flex flex-wrap items-center gap-1 mt-1" data-testid="client-associated-emails">
                <span className="text-[11px] text-muted-foreground">Email associate:</span>
                {emails.map((e) => (
                  <Badge key={e} variant="secondary" className="text-[10px] font-normal">{e}</Badge>
                ))}
              </div>
            )}
            <div className="flex gap-1 mt-1 flex-wrap items-center">{(c.tags||[]).map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}{c.allergies && <Badge variant="destructive">Allergia: {c.allergies}</Badge>}
              {s.open_debts_total > 0 && <Badge variant="outline" className="border-[hsl(38_92%_45%/0.5)] bg-[hsl(38_92%_50%/0.12)] text-[hsl(38_92%_35%)] gap-1" data-testid="client-insoluto-indicator"><AlertTriangle className="h-3 w-3" /> Insoluto {eur(s.open_debts_total)}</Badge>}
            </div>
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <DirectSaleDialog clientId={id} onDone={load} />
            <Button variant="outline" onClick={openEdit} data-testid="client-edit-button"><Pencil className="h-4 w-4 mr-1.5" /> Modifica profilo</Button>
            {isAdmin && <Button variant="outline" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={deleteProfile} data-testid="client-delete-button"><Trash2 className="h-4 w-4 mr-1.5" /> Elimina</Button>}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Spesa totale" value={eur(s.total_spend)} />
        <Stat label="Appuntamenti" value={num(s.appointments_count)} />
        <Stat label="Ticket medio" value={eur(s.avg_ticket)} />
        <Stat label="Punti" value={num(s.points)} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Panoramica</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="tab-appointments">Appuntamenti</TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales">Vendite</TabsTrigger>
          <TabsTrigger value="technical" data-testid="tab-technical">Scheda Tecnica</TabsTrigger>
          <TabsTrigger value="transformations" data-testid="tab-transformations">Trasformazioni</TabsTrigger>
          <TabsTrigger value="loyalty" data-testid="tab-loyalty">Loyalty & Wallet</TabsTrigger>
          <TabsTrigger value="memberships" data-testid="tab-memberships">Pacchetti & Abbonamenti</TabsTrigger>
          <TabsTrigger value="referral" data-testid="tab-referral">Porta un'Amica</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-5 space-y-3">
              <h3 className="font-heading text-lg font-semibold">Informazioni</h3>
              <Row label="Ultima visita" value={fmtDate(s.last_visit)} />
              <Row label="Prossimo appuntamento" value={fmtDate(s.next_appointment)} />
              <Row label="Cancellazioni" value={num(s.cancellations)} />
              <Row label="No-show" value={num(s.no_shows)} />
              <Row label="Consenso marketing" value={c.marketing_consent ? "Si" : "No"} />
              <Row label="Patch test" value={c.patch_test ? "Effettuato" : "Non effettuato"} />
              {c.notes && <div className="text-sm text-muted-foreground border-t border-border pt-2">{c.notes}</div>}
            </Card>
            <div className="grid grid-cols-1 gap-3">
              <Card className="p-4 flex items-center gap-3"><Award className="h-8 w-8 text-[hsl(var(--hi-gold))]" /><div><div className="text-xs text-muted-foreground">Punti fedelta</div><div className="font-heading text-xl font-semibold">{num(s.points)}</div></div></Card>
              <Card className="p-4 flex items-center gap-3"><Wallet className="h-8 w-8 text-[hsl(var(--hi-status-info))]" /><div><div className="text-xs text-muted-foreground">Credito</div><div className="font-heading text-xl font-semibold">{eur(s.credit)}</div></div></Card>
              <Card className="p-4 flex items-center gap-3"><Gift className="h-8 w-8 text-[hsl(var(--hi-status-success))]" /><div><div className="text-xs text-muted-foreground">Saldo Gift Card</div><div className="font-heading text-xl font-semibold">{eur(s.giftcard_balance)}</div></div></Card>
            </div>
          </div>

          {/* Saldo cliente: insoluti + crediti */}
          <Card className="p-5 mt-4" data-testid="client-balance-section">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <h3 className="font-heading text-lg font-semibold">Saldo cliente</h3>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => setAddCreditOpen(true)} data-testid="client-add-credit"><Plus className="h-4 w-4 mr-1" /> Aggiungi credito</Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-lg border border-[hsl(38_92%_45%/0.4)] bg-[hsl(38_92%_50%/0.08)] p-3" data-testid="client-open-debts">
                <div className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-[hsl(38_92%_42%)]" /> Insoluti aperti</div>
                <div className="font-heading text-2xl font-semibold mt-1">{eur(bal.open_debts_total)}</div>
              </div>
              <div className="rounded-lg border border-[hsl(var(--hi-status-success)/0.4)] bg-[hsl(var(--hi-status-success)/0.08)] p-3" data-testid="client-credit-available">
                <div className="text-xs text-muted-foreground flex items-center gap-1"><Wallet className="h-3.5 w-3.5 text-[hsl(var(--hi-status-success))]" /> Credito disponibile</div>
                <div className="font-heading text-2xl font-semibold mt-1">{eur(bal.credit_available)}</div>
              </div>
            </div>

            {(bal.debts || []).filter((d) => d.status !== "cancelled").length > 0 && (
              <div className="space-y-2 mb-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Insoluti</div>
                {(bal.debts || []).filter((d) => d.status !== "cancelled").map((d) => (
                  <div key={d.id} className="rounded-lg border border-border p-3" data-testid={`debt-${d.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <div className="text-sm font-medium">{eur(d.remaining)} <span className="text-xs text-muted-foreground font-normal">/ {eur(d.original_amount)} · {fmtDate(d.date)}</span></div>
                        <div className="text-xs text-muted-foreground">{(d.service_names || []).join(", ") || "—"}{d.note ? ` · ${d.note}` : ""}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-[10px]">{debtStatusLabel[d.status] || d.status}</Badge>
                        {canEdit && (d.status === "open" || d.status === "partial") && (
                          <>
                            <Button size="sm" className="h-8" onClick={() => openDebtPay(d)} data-testid={`debt-pay-${d.id}`}>Incassa insoluto</Button>
                            <Button size="sm" variant="ghost" className="h-8 text-destructive" onClick={() => cancelDebt(d)} data-testid={`debt-cancel-${d.id}`}>Annulla</Button>
                          </>
                        )}
                      </div>
                    </div>
                    {(d.payments || []).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-border/60 space-y-0.5">
                        {d.payments.map((p) => (
                          <div key={p.id} className="text-[11px] text-muted-foreground flex justify-between">
                            <span>{fmtDate(p.date)} · {p.method}{p.operator_name ? ` · ${p.operator_name}` : ""}</span>
                            <span className="text-[hsl(var(--hi-status-success))]">-{eur(p.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Movimenti saldo</div>
            {(bal.movements || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessun movimento.</p>
            ) : (
              <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="client-balance-movements">
                {(bal.movements || []).map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-border/40 last:border-0">
                    <div>
                      <span className="text-xs text-muted-foreground">{fmtDate(m.date)}</span>
                      <span className="ml-2">{m.label}</span>
                      {m.method ? <span className="text-xs text-muted-foreground"> · {m.method}</span> : null}
                      {m.note ? <span className="text-xs text-muted-foreground"> · {m.note}</span> : null}
                    </div>
                    <span className={`tabular font-medium ${m.sign === "+" ? "text-[hsl(38_92%_42%)]" : "text-[hsl(var(--hi-status-success))]"}`}>{m.sign}{eur(m.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="appointments" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-muted-foreground">Storico e prossimi appuntamenti di {c.first_name}</div>
            {canEdit && (
              <Button size="sm" onClick={() => nav(`/agenda?client=${id}`)} data-testid="client-book-appointment-button">
                <CalendarClock className="h-4 w-4 mr-1" /> Prenota appuntamento
              </Button>
            )}
          </div>
          <Card className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Servizi</TableHead><TableHead>Prezzo</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.appointments.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Nessun appuntamento</TableCell></TableRow>}
                {d.appointments.map((a) => <TableRow key={a.id}><TableCell>{fmtDateTime(a.start)}</TableCell><TableCell>{(a.service_names||[]).join(", ")}</TableCell><TableCell className="tabular">{eur(a.price)}</TableCell><TableCell><StatusBadge status={a.status} /></TableCell></TableRow>)}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="sales" className="mt-4">
          {canEdit && (
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-muted-foreground">Registra una vendita di pacchetti, abbonamenti o gift card.</p>
              <DirectSaleDialog clientId={id} onDone={load} />
            </div>
          )}
          <Card className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Articoli</TableHead><TableHead>Totale</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.sales.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">Nessuna vendita</TableCell></TableRow>}
                {d.sales.map((sl) => <TableRow key={sl.id}><TableCell>{fmtDate(sl.created_at)}</TableCell><TableCell>{(sl.lines||[]).map((l)=>l.name).join(", ")}</TableCell><TableCell className="tabular">{eur(sl.total)}</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="technical" className="mt-4">
          <div className="flex justify-between items-center mb-3">
            <div className="text-sm text-muted-foreground">{tc ? `Base ${tc.natural_base||"—"} · ${tc.white_percent||0}% bianchi · porosita ${tc.porosity||"—"}` : "Nessuna scheda tecnica"}</div>
            <Button size="sm" onClick={() => setFormulaOpen(true)} data-testid="add-formula-button"><Plus className="h-4 w-4 mr-1" /> Nuova formula</Button>
          </div>
          <div className="space-y-3">
            {(tc?.formulas||[]).length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground">Nessuna formula registrata.</Card>}
            {(tc?.formulas||[]).map((f) => (
              <Card key={f.id} className="p-4">
                <div className="flex justify-between items-start">
                  <span className="font-medium">{f.technique || "Formula"}</span>
                  <div className="flex items-center gap-2">
                    {f.appointment_date && <Badge variant="secondary" data-testid="formula-appt-badge">Appuntamento {fmtDate(f.appointment_date)}</Badge>}
                    <span className="text-xs text-muted-foreground">{fmtDate(f.date)}</span>
                  </div>
                </div>
                {(f.ingredients||[]).length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {f.ingredients.map((ing, idx) => (
                      <div key={idx} className="flex justify-between text-sm border-b border-border/50 last:border-0 py-1">
                        <span>{ing.product_name || "—"}</span>
                        <span className="tabular text-muted-foreground">{ing.qty != null ? ing.qty : ""} {ing.unit}</span>
                      </div>
                    ))}
                    <div className="text-sm pt-1"><span className="text-muted-foreground">Posa: </span>{f.processing_min||0}'</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm">
                    <div><span className="text-muted-foreground">Formula: </span>{f.formula||"—"}</div>
                    <div><span className="text-muted-foreground">Ossidante: </span>{f.oxidant||"—"}</div>
                    <div><span className="text-muted-foreground">Grammi: </span>{f.grams||"—"}</div>
                    <div><span className="text-muted-foreground">Posa: </span>{f.processing_min||0}'</div>
                    {f.toning && <div><span className="text-muted-foreground">Tono: </span>{f.toning}</div>}
                  </div>
                )}
                {f.notes && <div className="text-sm text-muted-foreground mt-2">{f.notes}</div>}
                {f.created_by_name && <div className="text-[11px] text-muted-foreground mt-1">Registrata da {f.created_by_name}</div>}
                <Button variant="ghost" size="sm" className="mt-2" onClick={() => { setFormula({ ...f, technique: (f.technique||"Formula") + " (copia)" }); setFormulaOpen(true); }}>Duplica</Button>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="transformations" className="mt-4" data-testid="transformations-panel">
          <Card className="p-4 mb-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Tipo</Label>
                <Select value={pKind} onValueChange={setPKind}>
                  <SelectTrigger className="w-[140px]" data-testid="photo-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="before">Prima</SelectItem>
                    <SelectItem value="after">Dopo</SelectItem>
                    <SelectItem value="other">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 flex-1 min-w-[180px]">
                <Label className="text-xs">Etichetta (es. servizio/data)</Label>
                <Input value={pLabel} onChange={(e) => setPLabel(e.target.value)} placeholder="Es. Balayage caramello" data-testid="photo-label" />
              </div>
              <Button asChild disabled={uploading} data-testid="photo-upload-button">
                <label className="cursor-pointer">
                  {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />}
                  {uploading ? "Caricamento..." : "Carica foto"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="photo-input" />
                </label>
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1"><ImageIcon className="h-3 w-3" /> Archivio privato: le foto sono visibili solo allo staff autorizzato e alla cliente nel suo portale.</p>
          </Card>

          {(() => {
            const befores = photos.filter((p) => p.kind === "before");
            const afters = photos.filter((p) => p.kind === "after");
            if (!befores.length || !afters.length) return null;
            const bId = cmpB || befores[0].id;
            const aId = cmpA || afters[0].id;
            const b = photos.find((p) => p.id === bId);
            const a = photos.find((p) => p.id === aId);
            return (
              <Card className="p-4" data-testid="client-compare">
                <div className="text-sm font-medium mb-3">Confronto Prima / Dopo</div>
                <div className="grid grid-cols-2 gap-2 mb-3 max-w-md">
                  <Select value={bId} onValueChange={setCmpB}><SelectTrigger data-testid="cmp-before"><SelectValue placeholder="Prima" /></SelectTrigger><SelectContent>{befores.map((p) => <SelectItem key={p.id} value={p.id}>{p.label || fmtDate(p.created_at)}</SelectItem>)}</SelectContent></Select>
                  <Select value={aId} onValueChange={setCmpA}><SelectTrigger data-testid="cmp-after"><SelectValue placeholder="Dopo" /></SelectTrigger><SelectContent>{afters.map((p) => <SelectItem key={p.id} value={p.id}>{p.label || fmtDate(p.created_at)}</SelectItem>)}</SelectContent></Select>
                </div>
                {b && a && <BeforeAfterSlider before={photoUrl(b.storage_path)} after={photoUrl(a.storage_path)} height={380} />}
              </Card>
            );
          })()}

          {photos.length === 0 ? (
            <Card className="p-10 text-center text-sm text-muted-foreground">Nessuna foto. Carica la prima trasformazione.</Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {photos.map((p) => (
                <Card key={p.id} className="p-0 overflow-hidden group relative" data-testid={`photo-card-${p.id}`}>
                  <button onClick={() => setViewer(p)} className="block w-full aspect-square bg-muted overflow-hidden">
                    <img src={photoUrl(p.storage_path)} alt={p.label || "foto"} loading="lazy" className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" />
                  </button>
                  <div className="absolute top-2 left-2">
                    <Badge variant={p.kind === "before" ? "secondary" : p.kind === "after" ? "default" : "outline"}>
                      {p.kind === "before" ? "Prima" : p.kind === "after" ? "Dopo" : "Foto"}
                    </Badge>
                  </div>
                  <Button variant="destructive" size="icon" className="h-7 w-7 absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => deletePhoto(p.id)} data-testid={`photo-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  <div className="p-2">
                    <div className="text-xs font-medium truncate">{p.label || "Senza etichetta"}</div>
                    <div className="text-[10px] text-muted-foreground">{fmtDate(p.created_at)}</div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="loyalty" className="mt-4">
          <Card className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>Motivo</TableHead><TableHead className="text-right">Movimento</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.ledgers.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Nessun movimento</TableCell></TableRow>}
                {d.ledgers.map((l) => <TableRow key={l.id}><TableCell>{fmtDate(l.created_at)}</TableCell><TableCell className="capitalize">{l.kind}</TableCell><TableCell className="text-muted-foreground">{l.reason}</TableCell><TableCell className={`text-right tabular ${l.delta>=0?"text-[hsl(var(--hi-status-success))]":"text-destructive"}`}>{l.delta>=0?"+":""}{l.kind==="points"?num(l.delta):eur(l.delta)}</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="memberships" className="mt-4" data-testid="memberships-panel">
          {(() => {
            const pkgs = (d.packages || []).filter((p) => p.active !== false);
            const subs = (d.client_subscriptions || []).filter((s) => s.active !== false);
            const gcs = (d.giftcards || []).filter((g) => g.active !== false && (g.balance || 0) > 0);
            const empty = pkgs.length === 0 && subs.length === 0 && gcs.length === 0;
            const remainLabel = (p) => {
              const arr = Array.isArray(p.remaining) ? p.remaining : Array.isArray(p.included) ? p.included : [];
              return arr.map((i) => `${Math.max(0, (i.qty || 0) - (i.used || 0))}/${i.qty} ${i.name}`).join(" · ");
            };
            return (
              <div className="space-y-5">
                <div>
                  <h3 className="font-heading text-lg font-semibold mb-2 flex items-center gap-2"><PkgIcon className="h-5 w-5 text-[hsl(var(--hi-gold))]" /> Pacchetti attivi</h3>
                  {pkgs.length === 0 ? <Card className="p-6 text-center text-sm text-muted-foreground">Nessun pacchetto attivo</Card> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {pkgs.map((p) => (
                        <Card key={p.id} className="p-4" data-testid={`client-package-${p.id}`}>
                          <div className="flex items-center justify-between"><span className="font-medium">{p.name}</span><Badge variant="secondary">{eur(p.price)}</Badge></div>
                          {remainLabel(p) && <div className="text-xs text-muted-foreground mt-1">{remainLabel(p)}</div>}
                          {p.expiry && <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Scade il {fmtDate(p.expiry)}</div>}
                          {p.created_at && <div className="text-[11px] text-muted-foreground mt-0.5">Acquistato il {fmtDate(p.created_at)}</div>}
                          {canEdit && (
                            <Button variant="ghost" size="sm" className="mt-2 h-7 text-xs text-destructive hover:text-destructive" onClick={() => openVoidPkg(p)} data-testid={`void-package-${p.id}`}>
                              <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Annulla pacchetto inserito per errore
                            </Button>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold mb-2 flex items-center gap-2"><RefreshCw className="h-5 w-5 text-[hsl(var(--hi-status-info))]" /> Abbonamenti attivi</h3>
                  {subs.length === 0 ? <Card className="p-6 text-center text-sm text-muted-foreground">Nessun abbonamento attivo</Card> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {subs.map((s) => (
                        <Card key={s.id} className="p-4" data-testid={`client-subscription-${s.id}`}>
                          <div className="flex items-center justify-between"><span className="font-medium">{s.name}</span><Badge variant="secondary">{eur(s.price)}</Badge></div>
                          <div className="flex flex-wrap gap-1 mt-2">{(s.benefits || []).map((b) => <Badge key={b} variant="outline" className="text-[10px]">{b}</Badge>)}</div>
                          {s.next_renewal && <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Rinnovo il {fmtDate(s.next_renewal)}</div>}
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold mb-2 flex items-center gap-2"><Gift className="h-5 w-5 text-[hsl(var(--hi-status-success))]" /> Gift Card</h3>
                  {gcs.length === 0 ? <Card className="p-6 text-center text-sm text-muted-foreground">Nessuna gift card attiva</Card> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {gcs.map((g) => (
                        <Card key={g.id} className="p-4" data-testid={`client-giftcard-${g.id}`}>
                          <div className="flex items-center justify-between"><span className="font-medium">{g.code}</span><span className="font-heading text-xl font-semibold">{eur(g.balance)}</span></div>
                          <div className="text-xs text-muted-foreground mt-1">su {eur(g.initial_amount)}{g.expiry ? ` · scade il ${fmtDate(g.expiry)}` : ""}</div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
                {empty && <p className="text-xs text-muted-foreground text-center">Usa il pulsante <b>Vendita diretta</b> in alto per assegnare un pacchetto, abbonamento o gift card.</p>}
              </div>
            );
          })()}
        </TabsContent>


        <TabsContent value="referral" className="mt-4" data-testid="referral-panel-tab">
          <ReferralPanel clientId={id} canEdit={canEdit} />
        </TabsContent>
      </Tabs>

      <Dialog open={formulaOpen} onOpenChange={setFormulaOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-2xl">Nuova formula</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2"><Label>Tecnica</Label><Input value={formula.technique} onChange={(e) => setFormula({ ...formula, technique: e.target.value })} data-testid="formula-technique" /></div>
            <div className="space-y-1.5"><Label>Formula</Label><Input value={formula.formula} onChange={(e) => setFormula({ ...formula, formula: e.target.value })} data-testid="formula-formula" /></div>
            <div className="space-y-1.5"><Label>Ossidante</Label><Input value={formula.oxidant} onChange={(e) => setFormula({ ...formula, oxidant: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Grammi</Label><Input value={formula.grams} onChange={(e) => setFormula({ ...formula, grams: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Posa (min)</Label><Input type="number" value={formula.processing_min} onChange={(e) => setFormula({ ...formula, processing_min: +e.target.value })} /></div>
            <div className="space-y-1.5 col-span-2"><Label>Note</Label><Textarea value={formula.notes} onChange={(e) => setFormula({ ...formula, notes: e.target.value })} /></div>
          </div>
          <Button onClick={addFormula} data-testid="formula-save">Salva formula</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-2xl" data-testid="photo-viewer">
          <DialogHeader><DialogTitle className="font-heading text-xl">{viewer?.label || "Trasformazione"} · {viewer?.kind === "before" ? "Prima" : viewer?.kind === "after" ? "Dopo" : "Foto"}</DialogTitle></DialogHeader>
          {viewer && <img src={photoUrl(viewer.storage_path)} alt={viewer.label || "foto"} className="w-full rounded-lg max-h-[70vh] object-contain bg-muted" />}
          <div className="text-xs text-muted-foreground">{fmtDateTime(viewer?.created_at)} · caricata da {viewer?.uploaded_by_name || "staff"}</div>
        </DialogContent>
      </Dialog>

      {/* Incasso insoluto */}
      <Dialog open={!!debtPay} onOpenChange={(o) => !o && setDebtPay(null)}>
        <DialogContent data-testid="debt-pay-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Incassa insoluto</DialogTitle></DialogHeader>
          {debtPay && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">Residuo: <b className="text-foreground">{eur(debtPay.remaining)}</b>{debtPay.service_names?.length ? <span> · {debtPay.service_names.join(", ")}</span> : null}</div>
              <div className="space-y-1.5"><Label>Importo da incassare</Label><Input type="number" value={debtAmt} onChange={(e) => setDebtAmt(e.target.value)} data-testid="debt-pay-amount" /></div>
              <div className="space-y-1.5"><Label>Metodo di pagamento</Label>
                <Select value={debtMethod} onValueChange={setDebtMethod}>
                  <SelectTrigger data-testid="debt-pay-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{BAL_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <p className="text-[11px] text-muted-foreground">L'incasso entra nella cassa di oggi senza generare nuovo fatturato servizi.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDebtPay(null)}>Annulla</Button>
                <Button onClick={submitDebtPay} disabled={busyBal} data-testid="debt-pay-confirm">{busyBal && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Incassa</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Aggiungi credito */}
      <Dialog open={addCreditOpen} onOpenChange={setAddCreditOpen}>
        <DialogContent data-testid="add-credit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Aggiungi credito</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Importo</Label><Input type="number" value={creditAmt} onChange={(e) => setCreditAmt(e.target.value)} data-testid="add-credit-amount" /></div>
            <div className="space-y-1.5"><Label>Motivo / nota</Label><Input value={creditNote} onChange={(e) => setCreditNote(e.target.value)} placeholder="Es. omaggio, rettifica" data-testid="add-credit-note" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddCreditOpen(false)}>Annulla</Button>
              <Button onClick={submitAddCredit} disabled={busyBal} data-testid="add-credit-confirm">{busyBal && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Aggiungi</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Annulla pacchetto inserito per errore */}
      <Dialog open={!!voidPkg} onOpenChange={(o) => { if (!o) setVoidPkg(null); }}>
        <DialogContent data-testid="void-package-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Annulla pacchetto inserito per errore</DialogTitle></DialogHeader>
          {voidPkg && (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg border border-border p-3 space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Pacchetto</span><span className="font-medium">{voidPkg.package?.name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Importo</span><span className="font-medium">{eur(voidPkg.package?.price || 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Acquistato il</span><span>{fmtDate(voidPkg.package?.created_at)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Vendita collegata</span><span>{voidPkg.sale ? `${eur(voidPkg.sale.total)} · ${fmtDate(voidPkg.sale.created_at)}` : "—"}</span></div>
                {voidPkg.same_name_count > 1 && <div className="text-[11px] text-[hsl(38_92%_35%)]">Attenzione: esistono {voidPkg.same_name_count} pacchetti con lo stesso nome. Verrà annullato SOLO questo.</div>}
              </div>
              <div>
                <div className="text-muted-foreground mb-1">Utilizzi</div>
                {voidPkg.usages?.length ? (
                  <div className="space-y-1">
                    {voidPkg.usages.map((u, i) => (
                      <div key={i} className="flex justify-between text-xs"><span>{u.name || "Servizio"}</span><span>{u.used}/{u.qty} usati</span></div>
                    ))}
                  </div>
                ) : <div className="text-xs text-muted-foreground">Nessun dettaglio utilizzi.</div>}
                {voidPkg.used_total > 0 && (
                  <div className="mt-2 rounded-md bg-[hsl(38_92%_50%/0.12)] border border-[hsl(38_92%_50%/0.3)] px-3 py-2 text-xs text-[hsl(38_92%_30%)]">
                    Il pacchetto risulta già utilizzato ({voidPkg.used_total} sedute). Le prestazioni effettuate NON verranno cancellate; confermando procedi comunque alla rettifica dell'entitlement.
                  </div>
                )}
                {voidPkg.sale_only_package ? (
                  <div className="mt-2 text-[11px] text-muted-foreground">La vendita collegata contiene solo questo pacchetto: verrà stornata (nessun rimborso registrato).</div>
                ) : voidPkg.sale ? (
                  <div className="mt-2 text-[11px] text-muted-foreground">La vendita contiene altre voci: verrà rimossa solo la riga del pacchetto e ricalcolato il totale.</div>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label>Motivazione (obbligatoria)</Label>
                <Textarea value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="Es. pacchetto duplicato per errore in cassa" data-testid="void-package-reason" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setVoidPkg(null)}>Annulla</Button>
                <Button variant="destructive" onClick={confirmVoidPkg} disabled={voidBusy || !voidReason.trim()} data-testid="void-package-confirm">{voidBusy ? "Annullamento…" : "Conferma annullamento"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modifica profilo cliente */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto hi-scroll" data-testid="client-edit-sheet">
          <SheetHeader><SheetTitle className="font-heading text-2xl">Modifica profilo</SheetTitle></SheetHeader>
          {edit && (
            <div className="space-y-4 mt-6">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Nome</Label><Input value={edit.first_name} onChange={(e) => setEdit({ ...edit, first_name: e.target.value })} data-testid="edit-first-name" /></div>
                <div className="space-y-1.5"><Label>Cognome</Label><Input value={edit.last_name} onChange={(e) => setEdit({ ...edit, last_name: e.target.value })} data-testid="edit-last-name" /></div>
              </div>
              <div className="space-y-1.5"><Label>Telefono</Label><PhoneField value={edit.phone} onChange={(v) => setEdit({ ...edit, phone: v })} testid="edit-phone" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="edit-email" /></div>
              <div className="space-y-1.5"><Label>Data di nascita</Label><Input type="date" value={edit.birthdate} onChange={(e) => setEdit({ ...edit, birthdate: e.target.value })} data-testid="edit-birthdate" /></div>
              <div className="space-y-1.5"><Label>Tag <span className="text-muted-foreground font-normal">(separati da virgola)</span></Label><Input value={edit.tags} onChange={(e) => setEdit({ ...edit, tags: e.target.value })} placeholder="VIP, Colore, Fedele" data-testid="edit-tags" /></div>
              <div className="space-y-1.5"><Label>Allergie / Note cliniche</Label><Input value={edit.allergies} onChange={(e) => setEdit({ ...edit, allergies: e.target.value })} placeholder="Es. PPD, ammoniaca" data-testid="edit-allergies" /></div>
              <div className="space-y-1.5">
                <Label>Come ci hai conosciuti? <span className="text-muted-foreground font-normal">(facoltativo)</span></Label>
                <Select value={edit.acquisition_source} onValueChange={(v) => { setEdit({ ...edit, acquisition_source: v }); if (v !== "amica") setReferrerClient(null); }}>
                  <SelectTrigger data-testid="edit-acquisition-source"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>{ACQUISITION_SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {edit.acquisition_source === "amica" && (
                <div className="space-y-2 rounded-lg border border-[hsl(var(--hi-gold)/0.4)] p-3 bg-[hsl(var(--hi-gold)/0.06)]" data-testid="edit-referrer-block">
                  <Label className="text-xs">Quale amica l'ha presentata? (cliente esistente)</Label>
                  <ClientSearch value={referrerClient} onSelect={setReferrerClient} />
                  <div className="text-[11px] text-muted-foreground">Non la trovi? Scrivi il nome: verrà segnata come "da confermare".</div>
                  <Input value={edit.referrer_name_raw} onChange={(e) => setEdit({ ...edit, referrer_name_raw: e.target.value })} placeholder="Nome amica (se non in elenco)" data-testid="edit-referrer-name-raw" className="h-8 text-sm" disabled={!!referrerClient} />
                </div>
              )}
              <div className="space-y-1.5"><Label>Note</Label><Textarea rows={3} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} data-testid="edit-notes" /></div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div><div className="text-sm font-medium">Consenso marketing</div><div className="text-xs text-muted-foreground">Email e WhatsApp promozionali</div></div>
                <Switch checked={edit.marketing_consent} onCheckedChange={(v) => setEdit({ ...edit, marketing_consent: v })} data-testid="edit-marketing" />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div><div className="text-sm font-medium">Patch test</div><div className="text-xs text-muted-foreground">Test allergologico effettuato</div></div>
                <Switch checked={edit.patch_test} onCheckedChange={(v) => setEdit({ ...edit, patch_test: v })} data-testid="edit-patch-test" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditOpen(false)}>Annulla</Button>
                <Button className="flex-1" onClick={saveEdit} disabled={saving} data-testid="edit-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva modifiche"}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const Row = ({ label, value }) => <div className="flex justify-between text-sm"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value}</span></div>;
